import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SideNavbar from "../../components/Navbar/SideNavbar";
import Header from "../../components/Header/Header";
import "../../components/Wrapper/Wrapper.scss";
import {
    getOrderChatbotAnalytics,
    getChatbotConversations,
} from "../../../core/actionCreators/orderChatbotAnalyticsActionCreator";
import {
    Table,
    Card,
    Row,
    Col,
    Statistic,
    Input,
    Select,
    Button,
    Tag,
    Space,
    Typography,
    DatePicker,
    Modal,
    Badge,
    Tooltip,
    Divider,
    message,
} from "antd";
import type { TablePaginationConfig } from "antd";
import {
    MessageOutlined,
    CalendarOutlined,
    WarningOutlined,
    ClockCircleOutlined,
    WifiOutlined,
    TeamOutlined,
    SearchOutlined,
    ReloadOutlined,
    EyeOutlined,
    CopyOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";

// @@ AI-COMMENT: Type definitions for analytics data structures
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface IntentDistribution {
    intent: string;
    count: number;
}

interface IntentHistory {
    intent: string;
    timestamp: string;
    context?: any;
}

interface Conversation {
    conversation_id?: string;
    conversation_length: number;
    full_conversation_text: string;
    created_at: string;
    updated_at: string;
    user_id: string;
    user_issue: string[] | null;
    user_feedback: any;
    intent_changes_history: IntentHistory[];
    order_ids?: number[];
    current_intent?: string | null;
    issue_text?: string | null;
    live_agent_enabled?: boolean;
    user_info?: {
        deviceCreated?: string | null;
        email?: string;
        firstName?: string;
        lastName?: string;
        phone?: string;
    };
    genai_usage?: {
        estimated_cost_inr: number;
        input_tokens: number;
        output_tokens: number;
        total_tokens: number;
    };
}

interface Analytics {
    total_chats?: number;
    today_chats?: number;
    chats_with_issues?: number;
    business_hours_chats?: number;
    non_business_hours_chats?: number;
    avg_response_time?: string;
    satisfaction_rate?: string;
    active_users?: number;
    intent_distribution?: IntentDistribution[];
    cta_clicks?: any;
}

interface ConversationsResponse {
    conversations?: Conversation[];
    pagination?: {
        current_page: number;
        total_pages: number;
        limit: number;
        total_count: number;
        original_total_count?: number;
        has_next: boolean;
        has_prev: boolean;
    };
}

interface FiltersState {
    page: number;
    limit: number;
    user_id: string;
    phone: string;
    intent: string;
    has_issue: boolean | undefined;
    search_str: string;
    start_date: string;
    end_date: string;
    search: string;
}

// @@ AI-COMMENT: Format date string to human-readable UTC format
const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "UTC",
    });
};

// @@ AI-COMMENT: Map raw intent key to display-friendly name
const getIntentDisplayName = (intent: string) => {
    const intentNames: { [key: string]: string } = {
        order_details: "Order Details",
        track_order: "Track Order",
        return_query: "Return Query",
        refund_query: "Refund Status",
        cancel_order: "Cancel Order",
        update_address: "Update Address",
        user_feedback: "User Feedback",
        list_orders: "List Orders",
        greeting: "Greeting",
        order_invoice: "Order Invoice",
        new_request: "New Request",
        return_exchange_query: "Return Exchange Query",
        new_request_query: "New Request",
        general_inquiry: "General Inquiry",
        refund_status: "Refund Status",
        raise_issue: "Raise Issue",
        delivery_timeline: "Delivery Timeline",
        order_cancellation: "Order Cancellation",
    };
    return (
        intentNames[intent] ||
        intent.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
    );
};

// @@ AI-COMMENT: Get the last intent from intent history array
const getLatestIntent = (intentHistory: IntentHistory[]) => {
    if (!intentHistory || intentHistory.length === 0) return "N/A";
    return getIntentDisplayName(
        intentHistory[intentHistory.length - 1].intent
    );
};

// @@ AI-COMMENT: Assign antd Tag color based on intent label
const getIntentTagColor = (intent: string): string => {
    const colorMap: { [key: string]: string } = {
        "Order Details": "blue",
        "Track Order": "green",
        "Return Query": "red",
        "Return Exchange Query": "volcano",
        "Refund Status": "orange",
        "Cancel Order": "red",
        "Update Address": "purple",
        "User Feedback": "gold",
        "Raise Issue": "magenta",
        "Delivery Timeline": "cyan",
        "Order Cancellation": "red",
        "General Inquiry": "geekblue",
        "New Request": "lime",
    };
    return colorMap[intent] || "default";
};

// @@ AI-COMMENT: Token types for chatbot transcript parsing
type MsgType = "user" | "assistant" | "options" | "order_details" | "system";

interface ParsedMessage {
    id: number;
    type: MsgType;
    sender: "user" | "bot";
    content: string;
    intent?: string;
    options?: string[];
    orderDetails?: string;
}

// @@ AI-COMMENT: Parse chatbot transcript format into structured message objects
const parseConversation = (text: string): ParsedMessage[] => {
    if (!text) return [];
    const lines = text.split("\n");
    const messages: ParsedMessage[] = [];
    let idx = 0;
    let i = 0;

    while (i < lines.length) {
        const line = lines[i].trim();
        if (!line) { i++; continue; }

        // @@ AI-COMMENT: Collect options block — items are indented lines below [options]
        if (line === "[options]") {
            const opts: string[] = [];
            i++;
            while (i < lines.length) {
                const ol = lines[i];
                const olTrimmed = ol.trim();
                if (!olTrimmed) { i++; break; }
                // @@ AI-COMMENT: Option items are indented with 2+ spaces and don't start with [
                if (/^\s{2,}\S/.test(ol) && !olTrimmed.startsWith("[")) {
                    opts.push(olTrimmed);
                    i++;
                } else break;
            }
            messages.push({ id: idx++, type: "options", sender: "bot", content: "", options: opts });
            continue;
        }

        // @@ AI-COMMENT: Parse order details block
        const orderMatch = line.match(/^\[order details\]\s*(.*)/i);
        if (orderMatch) {
            let detail = orderMatch[1];
            i++;
            // Collect continuation lines (indented)
            while (i < lines.length && /^\s{2,}/.test(lines[i])) {
                detail += "\n" + lines[i].trim();
                i++;
            }
            messages.push({ id: idx++, type: "order_details", sender: "bot", content: detail });
            continue;
        }

        // @@ AI-COMMENT: Parse assistant messages (interactive or conversational)
        const assistantMatch = line.match(/^\[(interactive|conversational|conversation)\]\s+assistant\s*(?:\[intent=([\w_]+)\])?:\s*(.*)/i);
        if (assistantMatch) {
            const intent = assistantMatch[2] || undefined;
            let content = assistantMatch[3] || "";
            i++;
            // Collect continuation lines that belong to this message
            while (i < lines.length) {
                const next = lines[i].trim();
                if (!next) { i++; break; }
                if (next.startsWith("[")) break;
                content += "\n" + next;
                i++;
            }
            messages.push({ id: idx++, type: "assistant", sender: "bot", content: content.trim(), intent });
            continue;
        }

        // @@ AI-COMMENT: Parse user messages (typed or button clicks)
        const userMatch = line.match(/^\[(interactive|conversational|conversation)\]\s+user:\s*(.*)/i);
        if (userMatch) {
            const raw = userMatch[2] || "";
            // Check if it's a button click
            const buttonMatch = raw.match(/^\[clicked button:\s*(.+)\]$/i);
            const typedMatch = raw.match(/^\[typed\]\s*(.*)/i);
            const content = buttonMatch ? buttonMatch[1] : typedMatch ? typedMatch[1] : raw;
            const isButton = !!buttonMatch;
            i++;
            messages.push({ id: idx++, type: "user", sender: "user", content: content.trim(), intent: isButton ? "button" : "typed" });
            continue;
        }

        // @@ AI-COMMENT: Skip unknown lines as system noise
        i++;
    }

    return messages;
};

// @@ AI-COMMENT: Modal component to display full conversation details
const ConversationModal = ({
    conversation,
    onClose,
}: {
    conversation: Conversation | null;
    onClose: () => void;
}) => {
    const [viewMode, setViewMode] = useState<"chat" | "raw">("chat");

    if (!conversation) return null;

    const handleCopy = () => {
        if (conversation.full_conversation_text) {
            navigator.clipboard.writeText(conversation.full_conversation_text);
            message.success("Copied to clipboard");
        }
    };

    const msgs = parseConversation(conversation.full_conversation_text);

    return (
        <Modal
            open={true}
            onCancel={onClose}
            footer={null}
            width={1200}
            title={
                <Space>
                    <MessageOutlined style={{ color: "#1677ff" }} />
                    <span>Conversation Details</span>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {conversation.user_id}
                    </Text>
                </Space>
            }
            styles={{ body: { padding: 0, overflow: "hidden" } }}
        >
            {/* @@ AI-COMMENT: Two-column layout: meta info left, transcript right */}
            <div style={{ display: "flex", height: "78vh", overflow: "hidden" }}>

                {/* @@ AI-COMMENT: Left panel — user meta, order tags, issue alert */}
                <div
                    style={{
                        width: 320,
                        flexShrink: 0,
                        borderRight: "1px solid #f0f0f0",
                        overflowY: "auto",
                        padding: "20px 16px",
                        background: "#fafafa",
                        display: "flex",
                        flexDirection: "column",
                        gap: 16,
                    }}
                >
                    {/* @@ AI-COMMENT: User info fields as labeled rows */}
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase", color: "#888", letterSpacing: 0.8, marginBottom: 10 }}>
                            User Info
                        </div>
                        {[
                            { label: "User ID", value: conversation.user_id },
                            { label: "Name", value: `${conversation.user_info?.firstName || ""} ${conversation.user_info?.lastName || ""}`.trim() || "N/A" },
                            { label: "Phone", value: conversation.user_info?.phone || "N/A" },
                            { label: "Email", value: conversation.user_info?.email || "N/A" },
                            { label: "Device", value: conversation.user_info?.deviceCreated || "N/A" },
                        ].map(({ label, value }) => (
                            <div key={label} style={{ display: "flex", flexDirection: "column", marginBottom: 10 }}>
                                <span style={{ fontSize: 11, color: "#aaa", fontWeight: 500 }}>{label}</span>
                                <span style={{ fontSize: 13, color: "#222", fontWeight: 500, wordBreak: "break-all" }}>{value}</span>
                            </div>
                        ))}
                    </div>

                    <Divider style={{ margin: "0" }} />

                    {/* @@ AI-COMMENT: Conversation timing and session info */}
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase", color: "#888", letterSpacing: 0.8, marginBottom: 10 }}>
                            Session
                        </div>
                        {[
                            { label: "Started", value: formatDate(conversation.created_at) },
                            { label: "Ended", value: formatDate(conversation.updated_at) },
                        ].map(({ label, value }) => (
                            <div key={label} style={{ display: "flex", flexDirection: "column", marginBottom: 10 }}>
                                <span style={{ fontSize: 11, color: "#aaa", fontWeight: 500 }}>{label}</span>
                                <span style={{ fontSize: 13, color: "#222", fontWeight: 500 }}>{value}</span>
                            </div>
                        ))}
                    </div>

                    <Divider style={{ margin: "0" }} />

                    {/* @@ AI-COMMENT: Order IDs section */}
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase", color: "#888", letterSpacing: 0.8, marginBottom: 8 }}>
                            Orders
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                            {conversation.order_ids && conversation.order_ids.length > 0
                                ? conversation.order_ids.map((id) => (
                                    <Tag key={id} color="purple">#{id}</Tag>
                                ))
                                : <span style={{ fontSize: 13, color: "#aaa" }}>N/A</span>}
                        </div>
                    </div>

                    {/* @@ AI-COMMENT: GenAI cost and token usage stats */}
                    {conversation.genai_usage && (
                        <>
                            <Divider style={{ margin: "0" }} />
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase", color: "#888", letterSpacing: 0.8, marginBottom: 8 }}>
                                    GenAI Usage
                                </div>
                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <span style={{ fontSize: 12, color: "#aaa" }}>Cost</span>
                                        <span style={{ fontSize: 13, fontWeight: 700, color: "#d46b08" }}>₹{conversation.genai_usage.estimated_cost_inr?.toFixed(4)}</span>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <span style={{ fontSize: 12, color: "#aaa" }}>Tokens</span>
                                        <span style={{ fontSize: 13, fontWeight: 500, color: "#555" }}>{conversation.genai_usage.total_tokens?.toLocaleString()}</span>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <span style={{ fontSize: 12, color: "#aaa" }}>Input</span>
                                        <span style={{ fontSize: 12, color: "#888" }}>{conversation.genai_usage.input_tokens?.toLocaleString()}</span>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <span style={{ fontSize: 12, color: "#aaa" }}>Output</span>
                                        <span style={{ fontSize: 12, color: "#888" }}>{conversation.genai_usage.output_tokens?.toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* @@ AI-COMMENT: Red alert section for user-reported issues */}
                    {conversation.issue_text && (
                        <>
                            <Divider style={{ margin: "0" }} />
                            <div
                                style={{
                                    background: "#fff1f0",
                                    border: "1px solid #ffa39e",
                                    borderLeft: "4px solid #f5222d",
                                    borderRadius: 6,
                                    padding: "10px 12px",
                                }}
                            >
                                <Text strong style={{ color: "#a8071a", fontSize: 11 }}>
                                    🚨 ISSUE REPORTED
                                </Text>
                                <div style={{ marginTop: 6 }}>
                                    <Text style={{ color: "#820014", fontSize: 12 }}>
                                        "{conversation.issue_text}"
                                    </Text>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* @@ AI-COMMENT: Right panel — conversation transcript */}
                <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    {/* @@ AI-COMMENT: Transcript toolbar with Chat/Raw toggle and copy button */}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "12px 16px",
                            borderBottom: "1px solid #f0f0f0",
                            background: "#fff",
                            flexShrink: 0,
                        }}
                    >
                        <Text strong style={{ fontSize: 13 }}>Conversation Transcript</Text>
                        <Space>
                            <Button.Group>
                                <Button
                                    size="small"
                                    type={viewMode === "chat" ? "primary" : "default"}
                                    onClick={() => setViewMode("chat")}
                                >
                                    Chat
                                </Button>
                                <Button
                                    size="small"
                                    type={viewMode === "raw" ? "primary" : "default"}
                                    onClick={() => setViewMode("raw")}
                                >
                                    Raw
                                </Button>
                            </Button.Group>
                            <Tooltip title="Copy transcript">
                                <Button size="small" icon={<CopyOutlined />} onClick={handleCopy} />
                            </Tooltip>
                        </Space>
                    </div>

                    {/* @@ AI-COMMENT: Scrollable chat message area */}
                    {viewMode === "chat" ? (
                        <div
                            style={{
                                flex: 1,
                                overflowY: "auto",
                                background: "#f0f2f5",
                                padding: "16px 14px",
                                display: "flex",
                                flexDirection: "column",
                                gap: 10,
                            }}
                        >
                            {msgs.map((msg) => {
                                // @@ AI-COMMENT: Render quick-reply option pills for bot option blocks
                                if (msg.type === "options") {
                                    return (
                                        <div key={msg.id} style={{ display: "flex", flexWrap: "wrap", gap: 6, paddingLeft: 40 }}>
                                            {(msg.options || []).map((opt, oi) => (
                                                <span
                                                    key={oi}
                                                    style={{
                                                        background: "#fff",
                                                        border: "1.5px solid #1677ff",
                                                        color: "#1677ff",
                                                        borderRadius: 20,
                                                        padding: "3px 12px",
                                                        fontSize: 12,
                                                        fontWeight: 500,
                                                        cursor: "default",
                                                    }}
                                                >
                                                    {opt}
                                                </span>
                                            ))}
                                        </div>
                                    );
                                }

                                // @@ AI-COMMENT: Render order detail block as a structured info card
                                if (msg.type === "order_details") {
                                    const parts = msg.content.split("|").map((p) => p.trim());
                                    return (
                                        <div
                                            key={msg.id}
                                            style={{
                                                background: "#fff",
                                                border: "1px solid #d9d9d9",
                                                borderLeft: "3px solid #722ed1",
                                                borderRadius: 8,
                                                padding: "10px 14px",
                                                fontSize: 12,
                                                color: "#444",
                                                margin: "4px 0",
                                            }}
                                        >
                                            <div style={{ fontWeight: 600, color: "#722ed1", marginBottom: 6, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>📦 Order Details</div>
                                            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                                {parts.map((p, pi) => {
                                                    const kv = p.split(":");
                                                    const key = kv[0]?.trim();
                                                    const val = kv.slice(1).join(":").trim();
                                                    return val ? (
                                                        <div key={pi} style={{ minWidth: 120 }}>
                                                            <span style={{ color: "#888", fontWeight: 500 }}>{key}: </span>
                                                            <span style={{ fontWeight: 600, color: "#222" }}>{val}</span>
                                                        </div>
                                                    ) : (
                                                        <div key={pi} style={{ color: "#555", width: "100%" }}>{p}</div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                }

                                // @@ AI-COMMENT: Render user messages (button clicks styled differently from typed)
                                if (msg.sender === "user") {
                                    const isButton = msg.intent === "button";
                                    return (
                                        <div key={msg.id} style={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-end", gap: 8 }}>
                                            <div
                                                style={{
                                                    maxWidth: "75%",
                                                    background: isButton ? "#e6f4ff" : "#1677ff",
                                                    color: isButton ? "#1677ff" : "#fff",
                                                    border: isButton ? "1.5px solid #91caff" : "none",
                                                    borderRadius: isButton ? "14px 14px 4px 14px" : "18px 18px 4px 18px",
                                                    padding: isButton ? "6px 14px" : "9px 14px",
                                                    fontSize: 13,
                                                    lineHeight: 1.55,
                                                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                                                    fontWeight: isButton ? 600 : 400,
                                                }}
                                            >
                                                {isButton && <div style={{ fontSize: 10, opacity: 0.6, marginBottom: 2 }}>🖱 Button Click</div>}
                                                {msg.content}
                                            </div>
                                            <div
                                                style={{
                                                    width: 28,
                                                    height: 28,
                                                    borderRadius: "50%",
                                                    background: "#1677ff",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: 12,
                                                    color: "#fff",
                                                    fontWeight: 700,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                U
                                            </div>
                                        </div>
                                    );
                                }

                                // @@ AI-COMMENT: Render bot assistant messages with avatar and intent badge
                                if (msg.sender === "bot") {
                                    return (
                                        <div key={msg.id} style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
                                            <div
                                                style={{
                                                    width: 28,
                                                    height: 28,
                                                    borderRadius: "50%",
                                                    background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: 14,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                🤖
                                            </div>
                                            <div style={{ maxWidth: "75%" }}>
                                                {msg.intent && (
                                                    <div style={{ marginBottom: 3 }}>
                                                        <span style={{ fontSize: 10, background: "#f9f0ff", color: "#722ed1", border: "1px solid #d3adf7", borderRadius: 10, padding: "1px 8px", fontWeight: 600 }}>
                                                            {getIntentDisplayName(msg.intent)}
                                                        </span>
                                                    </div>
                                                )}
                                                <div
                                                    style={{
                                                        background: "#fff",
                                                        border: "1px solid #e8e8e8",
                                                        borderRadius: "4px 18px 18px 18px",
                                                        padding: "9px 14px",
                                                        fontSize: 13,
                                                        lineHeight: 1.6,
                                                        color: "#222",
                                                        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                                                        whiteSpace: "pre-wrap",
                                                    }}
                                                >
                                                    {msg.content}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }

                                return null;
                            })}
                            {msgs.length === 0 && (
                                <div style={{ textAlign: "center", color: "#999", padding: 40 }}>
                                    No messages to display
                                </div>
                            )}
                        </div>
                    ) : (
                        <div
                            style={{
                                flex: 1,
                                overflowY: "auto",
                                background: "#1e1e1e",
                                padding: 16,
                                fontFamily: "monospace",
                                fontSize: 12,
                                color: "#d4d4d4",
                                whiteSpace: "pre-wrap",
                            }}
                        >
                            {conversation.full_conversation_text || "No conversation text available"}
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};

// @@ AI-COMMENT: Main Chatbot Analytics page component
const OrderChatbotAnalytics: React.FC = () => {
    const dispatch = useDispatch();
    const store: any = useSelector((s: any) => s);

    const {
        analytics,
        isLoading,
        error,
        conversations,
    }: {
        analytics: Analytics | null;
        isLoading: boolean;
        error: string | null;
        conversations: ConversationsResponse;
    } = store.orderChatbotAnalyticsStore;

    // @@ AI-COMMENT: Filter state including pagination and all filter fields
    const [filters, setFilters] = useState<FiltersState>({
        page: 1,
        limit: 15,
        user_id: "",
        phone: "",
        intent: "",
        has_issue: undefined as boolean | undefined,
        search_str: "",
        start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0],
        end_date: new Date().toISOString().split("T")[0],
        search: "",
    });

    const [selectedConversation, setSelectedConversation] =
        useState<Conversation | null>(null);
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
        dayjs().subtract(30, "day"),
        dayjs(),
    ]);

    // @@ AI-COMMENT: Auto-fetch data on mount and when date range changes
    useEffect(() => {
        dispatch(
            getOrderChatbotAnalytics({
                startDate: filters.start_date,
                endDate: filters.end_date,
            })
        );
        dispatch(getChatbotConversations(filters));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.start_date, filters.end_date]);

    // @@ AI-COMMENT: Fetch analytics summary data for the stat cards
    const fetchAnalytics = () => {
        dispatch(
            getOrderChatbotAnalytics({
                startDate: filters.start_date,
                endDate: filters.end_date,
            })
        );
    };

    // @@ AI-COMMENT: Fetch paginated conversations with active filters
    const fetchConversations = () => {
        dispatch(getChatbotConversations(filters));
    };

    const handleFilterChange = (field: string, value: any) => {
        setFilters((prev) => ({
            ...prev,
            [field]: value,
            ...(field !== "page" && { page: 1 }),
        }));
    };

    // @@ AI-COMMENT: Apply all active filters and refresh both analytics and conversations
    const applyFilters = () => {
        fetchAnalytics();
        fetchConversations();
    };

    // @@ AI-COMMENT: Reset all filters to defaults and re-fetch data
    const resetFilters = () => {
        const defaultFilters = {
            page: 1,
            limit: 15,
            user_id: "",
            phone: "",
            intent: "",
            has_issue: undefined as boolean | undefined,
            search_str: "",
            start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
                .toISOString()
                .split("T")[0],
            end_date: new Date().toISOString().split("T")[0],
            search: "",
        };
        setFilters(defaultFilters);
        setDateRange([dayjs().subtract(30, "day"), dayjs()]);
        setTimeout(() => {
            dispatch(
                getOrderChatbotAnalytics({
                    startDate: defaultFilters.start_date,
                    endDate: defaultFilters.end_date,
                })
            );
            dispatch(getChatbotConversations(defaultFilters));
        }, 50);
    };

    // @@ AI-COMMENT: Handle date range picker change and update filter state
    const handleDateRangeChange = (dates: any) => {
        if (dates && dates[0] && dates[1]) {
            setDateRange(dates);
            setFilters((prev) => ({
                ...prev,
                start_date: dates[0].format("YYYY-MM-DD"),
                end_date: dates[1].format("YYYY-MM-DD"),
                page: 1,
            }));
        }
    };

    // @@ AI-COMMENT: Handle antd Table pagination and sorting changes
    const handleTableChange = (pagination: TablePaginationConfig) => {
        const newFilters = {
            ...filters,
            page: pagination.current || 1,
            limit: pagination.pageSize || 15,
        };
        setFilters(newFilters);
        dispatch(getChatbotConversations(newFilters));
    };

    // @@ AI-COMMENT: Column definitions for the conversations Ant Design Table
    const columns = [
        {
            title: "User",
            key: "user",
            width: 180,
            render: (_: any, record: Conversation) => (
                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <Text strong style={{ fontSize: 13 }}>
                            {record.user_info?.firstName || "User"}{" "}
                            {record.user_info?.lastName || ""}
                        </Text>
                        {record.issue_text && (
                            <Badge dot status="error">
                                <Tag color="red" style={{ fontSize: 10, padding: "0 4px" }}>
                                    ISSUE
                                </Tag>
                            </Badge>
                        )}
                    </div>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                        {record.user_id}
                    </Text>
                </div>
            ),
        },
        {
            title: "Phone",
            key: "phone",
            width: 130,
            render: (_: any, record: Conversation) => (
                <Text style={{ fontSize: 12 }}>{record.user_info?.phone || "—"}</Text>
            ),
        },
        {
            title: "Email",
            key: "email",
            ellipsis: true,
            render: (_: any, record: Conversation) => (
                <Tooltip title={record.user_info?.email}>
                    <Text style={{ fontSize: 12 }}>{record.user_info?.email || "—"}</Text>
                </Tooltip>
            ),
        },
        {
            title: "Intent",
            key: "intent",
            width: 160,
            render: (_: any, record: Conversation) => {
                const intent = getLatestIntent(record.intent_changes_history);
                return (
                    <Tag color={getIntentTagColor(intent)} style={{ fontSize: 11 }}>
                        {intent}
                    </Tag>
                );
            },
        },
        {
            title: "Orders",
            key: "orders",
            width: 80,
            align: "center" as const,
            render: (_: any, record: Conversation) => (
                <Tag color="green" style={{ fontSize: 11 }}>
                    {record.order_ids?.length || 0}
                </Tag>
            ),
        },
        // {
        //     title: "GenAI Cost",
        //     key: "genai",
        //     width: 100,
        //     align: "right" as const,
        //     render: (_: any, record: Conversation) =>
        //         record.genai_usage ? (
        //             <Text style={{ fontSize: 12, color: "#d46b08" }}>
        //                 ₹{record.genai_usage.estimated_cost_inr.toFixed(4)}
        //             </Text>
        //         ) : (
        //             <Text type="secondary">—</Text>
        //         ),
        // },
        {
            title: "Date",
            key: "date",
            width: 150,
            render: (_: any, record: Conversation) => (
                <Text style={{ fontSize: 11 }}>{formatDate(record.updated_at)}</Text>
            ),
        },
        {
            title: "Action",
            key: "action",
            width: 100,
            fixed: "right" as const,
            render: (_: any, record: Conversation) => (
                <Button
                    type="link"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => setSelectedConversation(record)}
                >
                    View
                </Button>
            ),
        },
    ];

    // @@ AI-COMMENT: Stat card data array for the KPI summary row
    const statCards = [
        {
            title: "Total Chats",
            value: analytics?.total_chats ?? "—",
            icon: <MessageOutlined />,
            color: "#1677ff",
            bg: "#e6f4ff",
        },
        {
            title: "Today's Chats",
            value: analytics?.today_chats ?? "—",
            icon: <CalendarOutlined />,
            color: "#722ed1",
            bg: "#f9f0ff",
        },
        {
            title: "User Issues",
            value: analytics?.chats_with_issues ?? "—",
            icon: <WarningOutlined />,
            color: "#f5222d",
            bg: "#fff1f0",
        },
        {
            title: "Business Hours",
            value: analytics?.business_hours_chats ?? "—",
            icon: <ClockCircleOutlined />,
            color: "#52c41a",
            bg: "#f6ffed",
        },
        {
            title: "Off Hours",
            value: analytics?.non_business_hours_chats ?? "—",
            icon: <WifiOutlined />,
            color: "#fa8c16",
            bg: "#fff7e6",
        },
        {
            title: "Connect Agent",
            value: analytics?.cta_clicks?.["Connect With Agent"] ?? "—",
            icon: <TeamOutlined />,
            color: "#13c2c2",
            bg: "#e6fffb",
        },
    ];

    const totalCount =
        conversations?.pagination?.original_total_count ||
        conversations?.pagination?.total_count ||
        0;

    return (
        <div className="flex mainWrapper w-full">
            <div className="sidenav-wrapper">
                <SideNavbar />
            </div>
            <div className="right-container">
                <div className="header-container">
                    <Header />
                </div>
                <div className="body-container">
                    <div style={{ padding: "20px 24px", background: "#f5f6fa", minHeight: "100vh" }}>

                        {/* @@ AI-COMMENT: Page title row with date range picker */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                            <Title level={4} style={{ margin: 0 }}>
                                Chatbot Analytics
                            </Title>
                            <Space>
                                <RangePicker
                                    value={dateRange}
                                    onChange={handleDateRangeChange}
                                    format="YYYY-MM-DD"
                                    allowClear={false}
                                    presets={[
                                        { label: "Today", value: [dayjs(), dayjs()] },
                                        { label: "Last 7 Days", value: [dayjs().subtract(7, "day"), dayjs()] },
                                        { label: "Last 30 Days", value: [dayjs().subtract(30, "day"), dayjs()] },
                                        { label: "Last 90 Days", value: [dayjs().subtract(90, "day"), dayjs()] },
                                    ]}
                                />
                                <Button
                                    icon={<SearchOutlined />}
                                    type="primary"
                                    onClick={applyFilters}
                                    loading={isLoading}
                                >
                                    Apply
                                </Button>
                            </Space>
                        </div>

                        {/* @@ AI-COMMENT: KPI stat cards showing key metrics */}
                        <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
                            {statCards.map((card) => (
                                <Col key={card.title} xs={12} sm={8} md={4}>
                                    <Card
                                        size="small"
                                        style={{ borderRadius: 10, border: "1px solid #e8e8e8" }}
                                        bodyStyle={{ padding: "14px 16px" }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                            <div
                                                style={{
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: 8,
                                                    background: card.bg,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: card.color,
                                                    fontSize: 16,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {card.icon}
                                            </div>
                                            <Statistic
                                                title={
                                                    <span style={{ fontSize: 11, color: "#666" }}>
                                                        {card.title}
                                                    </span>
                                                }
                                                value={card.value}
                                                valueStyle={{ fontSize: 22, fontWeight: 700, color: "#1a1a1a" }}
                                            />
                                        </div>
                                    </Card>
                                </Col>
                            ))}
                        </Row>

                        {/* @@ AI-COMMENT: All conversations card with inline filters and table */}
                        <Card
                            style={{ borderRadius: 10, border: "1px solid #e8e8e8" }}
                            bodyStyle={{ padding: 0 }}
                        >
                            {/* @@ AI-COMMENT: Filter bar row above the conversations table */}
                            <div
                                style={{
                                    padding: "16px 20px",
                                    borderBottom: "1px solid #f0f0f0",
                                    background: "#fafafa",
                                    borderRadius: "10px 10px 0 0",
                                }}
                            >
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                                    <Space>
                                        <Text strong style={{ fontSize: 15 }}>
                                            All Conversations
                                        </Text>
                                        <Tag color="blue" style={{ borderRadius: 10 }}>
                                            {totalCount} total
                                        </Tag>
                                    </Space>
                                    <Button
                                        icon={<ReloadOutlined />}
                                        size="small"
                                        onClick={resetFilters}
                                    >
                                        Reset
                                    </Button>
                                </div>

                                {/* @@ AI-COMMENT: Inline filter controls row */}
                                <Row gutter={[10, 10]} align="middle">
                                    <Col xs={24} sm={12} md={4}>
                                        {/* @@ AI-COMMENT: Separate User ID filter input */}
                                        <Input
                                            prefix={<SearchOutlined style={{ color: "#aaa" }} />}
                                            placeholder="Search by User ID"
                                            value={filters.user_id}
                                            onChange={(e) => handleFilterChange("user_id", e.target.value)}
                                            onPressEnter={applyFilters}
                                            allowClear
                                            size="middle"
                                        />
                                    </Col>
                                    <Col xs={24} sm={12} md={4}>
                                        {/* @@ AI-COMMENT: Separate Phone number filter input */}
                                        <Input
                                            prefix={<SearchOutlined style={{ color: "#aaa" }} />}
                                            placeholder="Search by Phone"
                                            value={filters.phone}
                                            onChange={(e) => handleFilterChange("phone", e.target.value)}
                                            onPressEnter={applyFilters}
                                            allowClear
                                            size="middle"
                                        />
                                    </Col>
                                    <Col xs={24} sm={12} md={4}>
                                        <Select
                                            placeholder="Filter by Intent"
                                            value={filters.intent || undefined}
                                            onChange={(val) => handleFilterChange("intent", val || "")}
                                            allowClear
                                            style={{ width: "100%" }}
                                            size="middle"
                                        >
                                            {analytics?.intent_distribution?.map((item, i) => (
                                                <Option key={i} value={item.intent}>
                                                    {getIntentDisplayName(item.intent)} ({item.count})
                                                </Option>
                                            ))}
                                        </Select>
                                    </Col>
                                    <Col xs={24} sm={8} md={4}>
                                        <Select
                                            placeholder="Issues"
                                            value={
                                                filters.has_issue === undefined
                                                    ? undefined
                                                    : filters.has_issue
                                                        ? "true"
                                                        : "false"
                                            }
                                            onChange={(val) =>
                                                handleFilterChange(
                                                    "has_issue",
                                                    val === undefined ? undefined : val === "true"
                                                )
                                            }
                                            allowClear
                                            style={{ width: "100%" }}
                                            size="middle"
                                        >
                                            <Option value="true">With Issues</Option>
                                            <Option value="false">Without Issues</Option>
                                        </Select>
                                    </Col>
                                    <Col xs={24} sm={8} md={4}>
                                        <Select
                                            placeholder="Conversation Type"
                                            value={filters.search_str || undefined}
                                            onChange={(val) =>
                                                handleFilterChange("search_str", val || "")
                                            }
                                            allowClear
                                            style={{ width: "100%" }}
                                            size="middle"
                                        >
                                            <Option value="Connect With Agent">Connect With Agent</Option>
                                        </Select>
                                    </Col>
                                    <Col xs={24} sm={8} md={3}>
                                        <Button
                                            type="primary"
                                            icon={<SearchOutlined />}
                                            onClick={applyFilters}
                                            loading={isLoading}
                                            block
                                        >
                                            Search
                                        </Button>
                                    </Col>
                                </Row>
                            </div>

                            {/* @@ AI-COMMENT: Ant Design Table rendering all conversation rows */}
                            <Table
                                dataSource={conversations?.conversations || []}
                                columns={columns}
                                rowKey={(record, index) =>
                                    record.conversation_id || `row-${index}`
                                }
                                loading={isLoading}
                                scroll={{ x: 900 }}
                                size="middle"
                                pagination={{
                                    current: conversations?.pagination?.current_page || 1,
                                    pageSize: filters.limit,
                                    total: totalCount,
                                    showSizeChanger: true,
                                    pageSizeOptions: ["10", "15", "25", "50"],
                                    showTotal: (total, range) =>
                                        `${range[0]}-${range[1]} of ${total} conversations`,
                                }}
                                onChange={handleTableChange}
                                rowClassName={(record) =>
                                    record.issue_text ? "ant-table-row-issue" : ""
                                }
                                style={{ borderRadius: "0 0 10px 10px" }}
                            />
                        </Card>

                        {/* @@ AI-COMMENT: Error alert displayed if API call fails */}
                        {error && (
                            <div
                                style={{
                                    marginTop: 12,
                                    padding: "8px 12px",
                                    background: "#fff1f0",
                                    border: "1px solid #ffa39e",
                                    borderRadius: 6,
                                    color: "#cf1322",
                                    fontSize: 13,
                                }}
                            >
                                Error: {error}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* @@ AI-COMMENT: Conversation detail modal triggered by table row view button */}
            <ConversationModal
                conversation={selectedConversation}
                onClose={() => setSelectedConversation(null)}
            />
        </div>
    );
};

export default OrderChatbotAnalytics;